import React, { useState } from 'react';
import { Calculator, Copy, Trash2 } from 'lucide-react';

const SimpleInterestCalculatorTool = () => {
  const [principal, setPrincipal] = useState('');
  const [rate, setRate] = useState('');
  const [time, setTime] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const p = parseFloat(principal);
    const r = parseFloat(rate);
    const t = parseFloat(time);

    if (isNaN(p) || isNaN(r) || isNaN(t) || p <= 0 || t <= 0) {
      setResult(null);
      return;
    }

    // Simple Interest Formula: I = P × R × T
    const interest = p * (r / 100) * t;
    const totalAmount = p + interest;

    setResult({
      principal: p,
      rate: r,
      time: t,
      interest,
      totalAmount
    });
  };

  const copyResult = (value) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setPrincipal('');
    setRate('');
    setTime('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Principal ($)
            </label>
            <input
              type="number"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              placeholder="e.g., 10000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="principal-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Rate (% per year)
            </label>
            <input
              type="number"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="e.g., 5"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="rate-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Time (years)
            </label>
            <input
              type="number"
              step="0.5"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              placeholder="e.g., 3"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="time-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-interest-btn"
      >
        <Calculator size={18} />
        Calculate Interest
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="interest-results">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Interest Earned</span>
                <button
                  onClick={() => copyResult(result.interest)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-interest"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-yellow-400" data-testid="interest-result">
                {formatCurrency(result.interest)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Total Amount</span>
                <button
                  onClick={() => copyResult(result.totalAmount)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-total"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-green-400" data-testid="total-amount-result">
                {formatCurrency(result.totalAmount)}
              </div>
            </div>
          </div>

          {/* Calculation Summary */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Calculation Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Principal (P)</span>
                <span className="text-white font-medium">{formatCurrency(result.principal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Rate (R)</span>
                <span className="text-white font-medium">{result.rate}% per year</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Time (T)</span>
                <span className="text-white font-medium">{result.time} {result.time === 1 ? 'year' : 'years'}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Interest (I = P × R × T)</span>
                <span className="text-yellow-400 font-medium">{formatCurrency(result.interest)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total (P + I)</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.totalAmount)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formula Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Simple Interest Formula</h3>
        <div className="text-sm text-gray-400 font-mono bg-gray-900 p-3 rounded">
          Interest = P × R × T
        </div>
        <div className="text-xs text-gray-500 mt-2">
          Where P = Principal, R = Rate (as decimal), T = Time in years
        </div>
      </div>

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Simple vs Compound Interest</h3>
        <div className="text-sm text-gray-400 space-y-2">
          <div>• <span className="text-white">Simple Interest</span> - Interest calculated only on the principal amount</div>
          <div>• <span className="text-white">Compound Interest</span> - Interest calculated on principal + accumulated interest</div>
          <div className="text-xs text-gray-500 mt-2">Simple interest is commonly used for short-term loans and some savings accounts.</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-interest-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default SimpleInterestCalculatorTool;
